// frontend/src/components/librarian/ui/charts/AreaChart.tsx
import React from 'react';
import { AreaChart as RAreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { CHART_COLORS, CHART_TICK } from './palette';

interface Series { key: string; label: string; color?: string }

interface Props {
  data: Record<string, unknown>[];
  xKey: string;
  /** Drawn in order; the first is navy, the second gold unless `color` says otherwise. */
  series: Series[];
  height?: number;
  /** What the picture shows, for assistive tech. */
  label: string;
}

const DEFAULTS = [CHART_COLORS.navy, CHART_COLORS.gold, CHART_COLORS.success, CHART_COLORS.danger];

/** Several series over time as soft filled lines, with a legend. */
const AreaChart: React.FC<Props> = ({ data, xKey, series, height = 240, label }) => {
  const colored = series.map((s, i) => ({ ...s, color: s.color ?? DEFAULTS[i % DEFAULTS.length] }));
  return (
    <div>
      <ul className="flex flex-wrap justify-end gap-x-4 gap-y-1 mb-2 text-[11px] text-[var(--lib-ink-muted)]" aria-label="Series">
        {colored.map((s) => (
          <li key={s.key} className="flex items-center gap-1.5">
            <span aria-hidden className="h-2 w-2 rounded-full" style={{ background: s.color }} />
            {s.label}
          </li>
        ))}
      </ul>
      <div role="img" aria-label={label}>
        <ResponsiveContainer width="100%" height={height}>
          <RAreaChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <defs>
              {colored.map((s) => (
                <linearGradient key={s.key} id={`libArea-${s.key}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={s.color} stopOpacity={0.22} />
                  <stop offset="100%" stopColor={s.color} stopOpacity={0} />
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid vertical={false} stroke={CHART_COLORS.hairline} />
            <XAxis dataKey={xKey} tickLine={false} axisLine={false} tick={CHART_TICK} minTickGap={28} interval="preserveStartEnd" />
            <YAxis tickLine={false} axisLine={false} tick={CHART_TICK} width={32} allowDecimals={false} />
            <Tooltip
              contentStyle={{
                background: CHART_COLORS.navy, border: 'none', borderRadius: 6,
                color: '#F5F2EA', fontFamily: 'JetBrains Mono, monospace', fontSize: 11,
              }}
            />
            {colored.map((s) => (
              <Area
                key={s.key}
                type="monotone"
                dataKey={s.key}
                name={s.label}
                stroke={s.color}
                strokeWidth={2}
                fill={`url(#libArea-${s.key})`}
                dot={false}
                activeDot={{ r: 4, fill: s.color, stroke: '#fff', strokeWidth: 1 }}
              />
            ))}
          </RAreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default AreaChart;
